import { InvoiceModel } from '@frontend/api-invoices';
import { useTranslation, Trans } from '@frontend/i18n';
import { calculatePaidOrUnpaidInvoiceTotalWithSurcharge } from '@frontend/payments-invoice-calculations';
import type { TerminalPaymentStatus, ITerminalStrategyError } from '@frontend/payments-terminal-controller';
import { useInvoicePerson } from '../../../../../hooks/use-invoice-person';
import { ReaderStatusDisplay } from './reader-status-display';

type ReaderStatusAdapterProps = {
  status: TerminalPaymentStatus;
  isTerminalInUse: boolean;
  invoice: InvoiceModel | undefined;
  hasConfirmedSuccess: boolean;
  hasPollEnded: boolean;
  error?: ITerminalStrategyError;
  handleEndActivePayment: () => void;
};

export const ReaderStatusAdapter = ({
  status,
  error,
  isTerminalInUse,
  invoice,
  hasConfirmedSuccess,
  hasPollEnded,
  handleEndActivePayment,
}: ReaderStatusAdapterProps) => {
  const { t } = useTranslation('payments');

  const { personFullName } = useInvoicePerson(invoice);

  const billedAmount = invoice ? calculatePaidOrUnpaidInvoiceTotalWithSurcharge(invoice) : 0;

  const getVariantProps = (): Parameters<typeof ReaderStatusDisplay>[number] => {
    const defaultProps = {
      title: personFullName,
      amount: billedAmount,
    };

    // show error regardless of status?
    if (isTerminalInUse) {
      return {
        ...defaultProps,
        message: t('This terminal is in use.'),
        variant: 'actionable-error',
        actionLabel: t('Override Terminal Payment'),
        onActionClick: handleEndActivePayment,
      };
    } else if (error) {
      return {
        ...defaultProps,
        message: error.message,
        variant: 'error',
      };
    }

    switch (status) {
      case 'not_ready':
        return {
          ...defaultProps,
          message: t('Initializing payment...'),
          variant: 'initializing',
        };
      case 'initializing':
        return {
          ...defaultProps,
          message: t('Initializing payment...'),
          variant: 'initializing',
        };
      case 'connecting':
        return {
          ...defaultProps,
          message: t('Connecting to terminal...'),
          variant: 'initializing',
        };
      case 'waiting-input':
        return {
          ...defaultProps,
          message: t('Waiting for customer'),
          variant: 'waiting-input',
        };
      case 'processing':
        return {
          ...defaultProps,
          message: t('Processing'),
          variant: 'processing',
        };
      case 'error':
        return {
          ...defaultProps,
          message: t('Card Declined'),
          variant: 'error',
        };
      case 'success':
        if (hasConfirmedSuccess) {
          return {
            ...defaultProps,
            message: t('Payment Accepted'),
            variant: 'success',
          };
        }
        if (!hasPollEnded) {
          return {
            ...defaultProps,
            message: t('Payment Successful'),
            messageAlt: t('Confirming payment, please wait...'),
            variant: 'success_confirming',
          };
        }

        return {
          ...defaultProps,
          message: t('Payment Successful'),
          messageAlt: (
            <Trans to={t}>
              The payment is taking longer than anticipated. <b>Do not retry this request.</b> Simply refresh the
              Requests page in a few minutes to see the updated status."
            </Trans>
          ),
          variant: 'success_stale',
        };
      default: {
        const _exhaustiveCheck: never = status;
        return _exhaustiveCheck;
      }
    }
  };

  const { title, amount, message, messageAlt, variant, actionLabel, onActionClick } = getVariantProps();

  return (
    <ReaderStatusDisplay
      title={title}
      amount={amount}
      message={message}
      messageAlt={messageAlt}
      variant={variant}
      actionLabel={actionLabel}
      onActionClick={onActionClick}
    />
  );
};
